import { useEffect, useState, useCallback } from "react";
import { Warehouse, Building2, Search, Plus, Loader2, ChevronsUpDown, Check, ArrowRightLeft, Home } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

/**
 * Lagerort + Interner Vermerk (Feb-2026, User-Wunsch):
 * Aus DispoDetailsCard extrahiert, damit Lagerort & Vermerk gemeinsam mit dem
 * öffentlichen Standort (Adresse/Straße/PLZ) in EINER Sektion „Standort & Lagerort"
 * stehen. Beide Felder speichern über eigene Dispo-PATCH-Endpunkte (Edit-Modus).
 */
export default function DispoLocationNote({ listingId, dispoLocation, ownAddress, onSaved }) {
    const location = dispoLocation || null;
    const [locOpen, setLocOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [opts, setOpts] = useState({ internal: [], dealers: [] });
    const [loadingOpts, setLoadingOpts] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [creating, setCreating] = useState(false);

    // Feb-2026 (User-Wunsch) — eigene Händleradresse (Standort des Inserats)
    // immer als ERSTE Option im Lagerort-Dropdown; inkl. Stand-Platz (z. B.
    // „Halle 2, Box 14"), bisherige Adress-Anzeige bleibt erhalten.
    const ownLabel = (() => {
        const a = ownAddress || {};
        const line = [a.street, [a.postal_code, a.location].filter(Boolean).join(" ").trim()]
            .filter((x) => x && x.trim()).join(", ");
        const base = line || (a.location || "").trim();
        const bay = (a.stand_platz || "").trim();
        return bay ? (base ? `${base} · ${bay}` : bay) : base;
    })();

    const fetchOpts = useCallback(async (term) => {
        setLoadingOpts(true);
        try {
            const { data } = await api.get("/dealer/dispo/location-options", { params: term ? { search: term } : {} });
            setOpts({ internal: data.internal || [], dealers: data.dealers || [] });
        } catch {
            setOpts({ internal: [], dealers: [] });
        } finally {
            setLoadingOpts(false);
        }
    }, []);

    useEffect(() => {
        if (!locOpen) return;
        const id = setTimeout(() => fetchOpts(search), 250);
        return () => clearTimeout(id);
    }, [locOpen, search, fetchOpts]);

    const chooseLocation = async (type, id, label) => {
        try {
            const { data } = await api.patch(`/listings/${listingId}/dispo/location`, { type, id, label });
            if (data.handover) toast.success(`Übergabe an „${label}" angefragt — Händler benachrichtigt`);
            else toast.success(`Lagerort gesetzt: ${label}`);
            setLocOpen(false);
            setSearch("");
            onSaved?.(data.location);
        } catch {
            toast.error("Lagerort konnte nicht gesetzt werden");
        }
    };

    const createInternal = async () => {
        const code = newCode.trim();
        if (!code) return;
        setCreating(true);
        try {
            const { data } = await api.post("/dealer/storage-locations", { code });
            await chooseLocation("internal", data.id, data.name || data.code || code);
            setNewCode("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Standort-Anlage fehlgeschlagen");
        } finally {
            setCreating(false);
        }
    };

    return (
        <>
            {/* Lagerort (interner Lager-/Übergabeort) */}
            <div className="md:col-span-1" data-testid="dispo-location-note-block">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Standort</label>
                <Popover open={locOpen} onOpenChange={setLocOpen}>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="flex h-11 w-full items-center justify-between gap-2 rounded-sm border border-gray-300 bg-white px-3 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
                            data-testid="dispo-location-trigger"
                        >
                            <span className={`truncate inline-flex items-center gap-2 ${!location ? "text-gray-400" : ""}`}>
                                {location?.type === "dealer"
                                    ? <Building2 className="w-4 h-4 text-violet-600" />
                                    : location?.type === "self"
                                        ? <Home className="w-4 h-4 text-emerald-600" />
                                        : <Warehouse className="w-4 h-4 text-gray-500" />}
                                {location ? location.label : "Lagerort wählen…"}
                                {location?.type === "dealer" && <span className="text-[10px] font-bold uppercase text-violet-600">Händler</span>}
                            </span>
                            <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[340px]" align="start">
                        <div className="flex items-center border-b px-3">
                            <Search className="w-4 h-4 mr-2 opacity-50" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Standort oder Händler suchen…"
                                className="h-10 border-0 focus-visible:ring-0 px-0"
                                data-testid="dispo-location-search"
                            />
                        </div>
                        <div className="max-h-[280px] overflow-y-auto py-1">
                            {/* Eigene Händleradresse IMMER zuerst (unabhängig vom Laden der Optionen) */}
                            {ownLabel && (
                                <div>
                                    <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50">Eigene Händleradresse</div>
                                    <button type="button" onClick={() => chooseLocation("self", "self", ownLabel)}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-emerald-50" data-testid="loc-self">
                                        <Home className="w-4 h-4 text-emerald-600 shrink-0" />
                                        <span className="truncate">{ownLabel}</span>
                                        {location?.type === "self" && <Check className="w-4 h-4 ml-auto text-emerald-600" />}
                                    </button>
                                </div>
                            )}
                            {loadingOpts ? (
                                <div className="py-6 text-center text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline" /> Lädt…</div>
                            ) : (
                                <>
                                    {opts.internal.length > 0 && (
                                        <div>
                                            <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50">Interne Standorte</div>
                                            {opts.internal.map((l) => (
                                                <button key={l.id} type="button" onClick={() => chooseLocation("internal", l.id, l.label)}
                                                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50" data-testid={`loc-internal-${l.id}`}>
                                                    <Warehouse className="w-4 h-4 text-gray-400" />
                                                    <span className="truncate">{l.label}</span>
                                                    {l.code && l.code !== l.label && <span className="text-[10px] text-gray-400 font-mono-num">{l.code}</span>}
                                                    {location?.type === "internal" && location.id === l.id && <Check className="w-4 h-4 ml-auto text-emerald-600" />}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {opts.dealers.length > 0 && (
                                        <div>
                                            <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-50">Registrierte Händler (Übergabe)</div>
                                            {opts.dealers.map((d) => (
                                                <button key={d.id} type="button" onClick={() => chooseLocation("dealer", d.id, d.label)}
                                                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-violet-50" data-testid={`loc-dealer-${d.id}`}>
                                                    <Building2 className="w-4 h-4 text-violet-500" />
                                                    <span className="truncate">{d.label}</span>
                                                    {(d.postal_code || d.city) && <span className="text-[10px] text-gray-400">{[d.postal_code, d.city].filter(Boolean).join(" ")}</span>}
                                                    {location?.type === "dealer" && location.id === d.id && <Check className="w-4 h-4 ml-auto text-emerald-600" />}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {opts.internal.length === 0 && opts.dealers.length === 0 && !ownLabel && (
                                        <div className="py-4 text-center text-sm text-gray-400">Keine Treffer</div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="border-t p-2 flex items-center gap-2 bg-gray-50">
                            <Input
                                value={newCode}
                                onChange={(e) => setNewCode(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createInternal(); } }}
                                placeholder="Neuer interner Standort (Code)…"
                                className="h-9 text-sm"
                                data-testid="dispo-location-new-code"
                            />
                            <Button type="button" size="sm" onClick={createInternal} disabled={creating || !newCode.trim()}
                                className="h-9 shrink-0 bg-[#111827] hover:bg-black text-white" data-testid="dispo-location-create">
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            </Button>
                        </div>
                    </PopoverContent>
                </Popover>
                {location?.type === "dealer" && location?.handover_status === "pending" && (
                    <p className="mt-1.5 text-[11px] text-violet-700 inline-flex items-center gap-1" data-testid="dispo-handover-pending">
                        <ArrowRightLeft className="w-3.5 h-3.5" /> Übergabe an Händler angefragt — Bestätigung ausstehend
                    </p>
                )}
            </div>
        </>
    );
}
